import { useMemo } from "react";
import { licenseStatusOf } from "./licenseStatus";
import { useDatabaseHash } from "./useDatabaseHash";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { PageLink } from "@/constants/groupConfig";
import { useGroups } from "@/hooks/groups/useGroups";

export interface LicensedPages {
  /** The pages this reader may open, in the order they were given. */
  pages: PageLink[];
  /** Whether every page has been decided. Until then `pages` is empty. */
  isSettled: boolean;
}

/**
 * Which of a set of pages this reader may open.
 *
 * All the groups in one read and the database hashed once, then a comparison per page. The
 * launcher used to mount an invisible component per page so that each could call the one-page
 * check — a hook, and therefore impossible to call in a loop — and report its answer back into
 * state. That meant a group lookup per page and an answer that arrived in twenty pieces.
 *
 * @param allPages - Every page that exists. Must be stable.
 */
export function useLicensedPages(allPages: readonly PageLink[]): LicensedPages {
  const { me } = useGeotabClient();
  const { data: groups, isFetched: groupsFetched } = useGroups();
  const { data: databaseHash } = useDatabaseHash();

  const commentsByGroupName = useMemo(() => {
    const comments = new Map<string, string | null>();
    for (const group of groups ?? []) {
      if (group.name) {
        comments.set(group.name, group.comments ?? null);
      }
    }
    return comments;
  }, [groups]);

  return useMemo(() => {
    const decided: PageLink[] = [];
    let settled = true;

    for (const page of allPages) {
      const status = licenseStatusOf({
        isDev: import.meta.env.DEV,
        userName: me?.name,
        groupComments: commentsByGroupName.get(page.groupName) ?? null,
        groupFetched: groupsFetched,
        databaseHash,
      });

      if (status === "pending") {
        settled = false;
      } else if (status === "licensed") {
        decided.push(page);
      }
    }

    // An answer for some of the pages is not an answer: a folder saying "9 pages" that opens to
    // show seven is lying, and the count is all a closed folder says.
    return settled ? { pages: decided, isSettled: true } : { pages: [], isSettled: false };
  }, [allPages, commentsByGroupName, databaseHash, groupsFetched, me?.name]);
}
